"use client"

import FeaturedPost from "@/(admin)/modules/ui-builder/config/components/FeaturedPost"
import VideoModal from "@/(admin)/modules/ui-builder/config/components/VideoModal"
import { HeroBannerWithBreadcrumbContent } from "@/(admin)/modules/ui-builder/config/themes/tamda-one/HeroBannerWithBreadcrumb/components/HeroBannerWithBreadcrumbContent"
import AllPosts from "@/components/all-posts"
import BreakingPosts from "@/components/breaking-posts"
import CategoryBanner from "@/components/category-banner"
import Pagination from "@/components/Pagination"
import SidePosts from "@/components/side-posts"
import AllPostsSkeleton from "@/components/skeletons/all-posts-skeleton"
import BreakingPostsSkeleton from "@/components/skeletons/breaking-posts-skeleton"
import FeaturedPostSkeleton from "@/components/skeletons/featured-post-skeleton"
import SidePostsSkeleton from "@/components/skeletons/side-posts-skeleton"
import SubFeaturePostsSkeleton from "@/components/skeletons/sub-feature-posts-skeleton"
import SubFeaturePosts from "@/components/sub-feature-posts"
import { FIRST_PAGE_SPECIAL_POSTS, PAGINATION_PAGE_SIZE } from "@/constants/constant"
import { usePostByCategory } from "@/hook/usePostByCategory"
import { t } from "@/languages/i18n"
import useIsMobile from "@/lib/config/lib/use-is-mobile"
import { PostByCategoryPageProps, PostSubData } from "@/types/types"
import { useRouter } from "next/navigation"
import { useMemo, useState } from "react"

const PostByCategoryPage = ({
  posts,
  meta,
  breakingPosts = [],
  categoryName,
  categorySlug = "",
  locale = "vi",
  currentPage = 1,
  isVideoPage = false,
  youtubeUrlMap = {},
  siteId,
  heroBannerUrl,
  banner,
}: PostByCategoryPageProps) => {
  const isMobile = useIsMobile()
  const router = useRouter()
  const [modalVideo, setModalVideo] = useState<string>("")
  const [isModalOpen, setIsModalOpen] = useState(false)

  const {
    featuredPost,
    subFeaturePosts,
    sidePosts,
    allPosts,
    displayCategoryName: hookCategoryName,
    displayCurrentPage,
    displayMeta,
    handlePageChange: hookHandlePageChange,
    postsSectionRef,
    loading,
  } = usePostByCategory(posts, meta, currentPage, categorySlug, locale, isVideoPage)

  const openModal = (youtubeUrl: string) => {
    setModalVideo(youtubeUrl)
    setIsModalOpen(true)
  }

  const closeModal = () => {
    setIsModalOpen(false)
    setModalVideo("")
  }

  const isCategoryPage = !isVideoPage && categorySlug && categorySlug !== "search" && categorySlug !== "video"

  const onPageChangeHandler = (page: number) => {
    if (isCategoryPage) {
      hookHandlePageChange(page)
      return
    }

    let basePath = ""
    if (categorySlug === "video") {
      basePath = `/${locale}/video`
    } else if (categorySlug === "search") {
      const urlParams = new URLSearchParams(window.location.search)
      const query = urlParams.get("q") || ""
      basePath = `/${locale}/search?q=${encodeURIComponent(query)}`
      const url = page === 1 ? basePath : `${basePath}&page=${page}`
      router.push(url)
      return
    } else {
      basePath = `/${locale}/category/${categorySlug}`
    }
    const url = page === 1 ? basePath : `${basePath}?page=${page}`
    router.push(url)
  }

  const totalPagesForAllPosts = useMemo(() => {
    if (displayMeta.pagination.pageCount) {
      return displayMeta.pagination.pageCount
    }
    const total = displayMeta.pagination.total || 0
    const pageSize = displayMeta.pagination.pageSize
    if (pageSize && categorySlug === "search") {
      return Math.max(1, Math.ceil(total / pageSize))
    }
    const postsPerPage = FIRST_PAGE_SPECIAL_POSTS + PAGINATION_PAGE_SIZE
    return Math.max(1, Math.ceil(total / postsPerPage))
  }, [displayMeta.pagination.total, displayMeta.pagination.pageSize, displayMeta.pagination.pageCount, categorySlug])

  const finalCategoryName = categoryName || hookCategoryName
  // Cụm tamdaone: hero banner breadcrumb (giống post detail). tamdamedia: promo banner.
  const isTamdaOne = siteId !== undefined && siteId !== "tamdamedia.eu"

  return (
    <>
      {isTamdaOne && heroBannerUrl && (
        <HeroBannerWithBreadcrumbContent
          id="category-hero-banner"
          image={heroBannerUrl}
          title={finalCategoryName || ""}
          breadcrumb={[
            { title: t("TRANG CHỦ"), link: "/" },
            { title: (finalCategoryName || "").toUpperCase(), link: `/category/${categorySlug}` },
          ]}
          isTamdaOne
        />
      )}
      <div className="max-w-[1160px] mx-auto px-4 sm:pt-[56px] sm:pb-[90px] pt-5 pb-10 flex flex-col sm:gap-12 gap-3">
        {!isTamdaOne && (
          <div className="w-full">
            {/* Banner đã được fetch ở server (fetchCategoryBannerData mặc định
                CATEGORY/TOP_CONTENT) và truyền xuống qua prop → render ngay trong
                HTML SSR, hết flash. Chỉ khi không có prop mới fallback autoFetch client. */}
            <CategoryBanner isMobile={isMobile} banner={banner} autoFetch={!banner} page="CATEGORY" position="TOP_CONTENT" />
          </div>
        )}
        <div className="flex flex-col">
          <div className="relative">
            <div className="flex flex-col gap-16 relative">
              <div className="flex flex-col w-full sm:gap-4 gap-5">
                {finalCategoryName && !isTamdaOne && (
                  <div className="flex flex-col items-start relative">
                    <h1 className="sm:text-base text-xl font-bold text-public-fg-primary uppercase leading-tight">
                      {finalCategoryName}
                    </h1>
                  </div>
                )}
                <div ref={postsSectionRef} className="flex flex-col lg:flex-row sm:gap-4 gap-6 w-full">
                  <div className="flex flex-col sm:gap-5 gap-2 lg:max-w-[846px] w-full">
                    {loading ? (
                      <>
                        <FeaturedPostSkeleton />
                        <SubFeaturePostsSkeleton />
                      </>
                    ) : (
                      <>
                        {featuredPost ? (
                          <FeaturedPost
                            post={featuredPost}
                            className="flex flex-col lg:flex-row w-full"
                            contentClassName="lg:max-w-[287.33px] w-full flex flex-col sm:p-4 p-2 group-hover:bg-transparent sm:w-[34.05%]"
                            imageClassName="sm:h-[319px] h-[195px] sm:w-[65.95%]"
                            titleClassName="sm:text-xl sm:leading-[31px] text-base leading-[24.8px] sm:font-bold font-medium text-black"
                            descriptionClassName="sm:text-sm sm:leading-[21.7px] text-xs leading-[18.6px] text-black "
                            dateClassName="sm:text-xs sm:leading-[21.7px] text-[10px] leading-[15.5px] text-black font-normal"
                            isVideo={isVideoPage}
                            youtubeUrl={isVideoPage ? youtubeUrlMap[featuredPost.id] : undefined}
                            onVideoClick={isVideoPage && youtubeUrlMap[featuredPost.id] ? openModal : undefined}
                          />
                        ) : (
                          <div className="w-full min-h-[319px] bg-transparent" />
                        )}
                        <SubFeaturePosts
                          posts={subFeaturePosts as PostSubData[]}
                          locale={locale}
                          isVideo={isVideoPage}
                          youtubeUrlMap={youtubeUrlMap}
                          onVideoClick={isVideoPage ? openModal : undefined}
                        />
                      </>
                    )}
                  </div>

                  <div className="lg:max-w-[266px] w-full">
                    {loading ? (
                      <SidePostsSkeleton />
                    ) : (
                      <SidePosts
                        posts={sidePosts}
                        locale={locale}
                        isVideo={isVideoPage}
                        youtubeUrlMap={youtubeUrlMap}
                        onVideoClick={isVideoPage ? openModal : undefined}
                      />
                    )}
                  </div>
                </div>

                <div className="flex flex-col lg:flex-row border-t border-public-border-subtle w-full max-w-[1144px]">
                  <div className="flex flex-col gap-5 sm:w-[66.2%] w-full sm:pt-0 pt-3 ">
                    {loading ? (
                      <AllPostsSkeleton />
                    ) : (
                      <>
                        <AllPosts
                          posts={allPosts}
                          locale={locale}
                          isVideo={isVideoPage}
                          youtubeUrlMap={youtubeUrlMap}
                          onVideoClick={isVideoPage ? openModal : undefined}
                        />
                        {totalPagesForAllPosts > 1 && (
                          <div className="sm:mt-[1.625rem] mt-4">
                            <Pagination
                              currentPage={displayCurrentPage}
                              totalPages={totalPagesForAllPosts}
                              onPageChange={onPageChangeHandler}
                            />
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  <div className="w-full lg:w-[33.8%] lg:border-l border-public-border-subtle sm:pl-2 sm:py-2 sm:px-2 pt-10">
                    {loading ? <BreakingPostsSkeleton /> : <BreakingPosts posts={breakingPosts} locale={locale} />}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        {isVideoPage && <VideoModal isOpen={isModalOpen} videoUrl={modalVideo} onClose={closeModal} />}
      </div>
    </>
  )
}

export default PostByCategoryPage
